/**
 * Teacher Assignment Modal Component
 *
 * Allows admins to assign/remove teachers to/from course groups.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { ModalOverlay, ModalContainer } from './BaseModal';
import {
  listTeachers,
  addTeacher,
  updateAssignment,
  updateTeacherSubjects,
  removeTeacher,
  listAvailableTeachers,
  setScheduleRelief,
  clearScheduleRelief
} from '../../api/courseGroupTeacherApi';
import { calculateWeeks } from '../../api/courseGroupMaterialApi';
import { getCourseGroup, updateCourseGroup } from '../../api/courseGroupPortalApi';
import {
  byBestMatch, teacherOptionLabel, matchTeacher, matchNote, anyoneHasAvailability
} from '../../utils/teacherAvailability';

const DAY_LABELS = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const isSpecialTerm = (term) => term === 0 || String(term) === '0';

// Styled components
const Modal = styled(ModalContainer)`
  max-width: 700px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 12px 12px 0 0;

  h3 {
    margin: 0;
    font-size: 18px;
    color: white;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: white;
  line-height: 1;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FormRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: end;
  margin-bottom: 16px;
`;

const FormGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const Button = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  background: ${props => props.$color || '#3b82f6'};
  color: white;
  white-space: nowrap;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TeacherCard = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TeacherInfo = styled.div`
  flex: 1;

  h5 {
    margin: 0 0 4px 0;
    font-size: 15px;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .meta {
    font-size: 12px;
    color: #6b7280;
  }
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => props.$bg || '#dbeafe'};
  color: ${props => props.$color || '#1e40af'};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const SmallButton = styled.button`
  padding: 6px 12px;
  border: 1px solid ${props => props.$borderColor || '#3b82f6'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  background: ${props => props.$bg || '#eff6ff'};
  color: ${props => props.$color || '#2563eb'};

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RemoveButton = styled(SmallButton)`
  border-color: #ef4444;
  background: #fef2f2;
  color: #dc2626;

  &:hover:not(:disabled) {
    background: #fee2e2;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: #6b7280;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 8px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 32px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 6px;
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const AssignedByInfo = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
`;

const SubjectCheckboxContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`;

const SubjectCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  padding: 4px 8px;
  background: ${props => props.$checked ? '#dbeafe' : 'white'};
  border: 1px solid ${props => props.$checked ? '#3b82f6' : '#d1d5db'};
  border-radius: 4px;

  input {
    width: 14px;
    height: 14px;
    cursor: pointer;
  }

  &:hover {
    background: ${props => props.$checked ? '#bfdbfe' : '#f3f4f6'};
  }
`;

const SubjectTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const SubjectTag = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  background: #e0e7ff;
  color: #4338ca;
`;

const NoSubjectsWarning = styled.span`
  font-size: 11px;
  color: #f59e0b;
  font-style: italic;
`;

// Shown above everything else when a schedule row has no main teacher: no teacher can
// see that booking's materials outside its relief weeks until one is set.
const UnstaffedWarning = styled.div`
  margin-bottom: 24px;
  padding: 12px 16px;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  background: #fef2f2;
  color: #991b1b;
  font-size: 13px;

  .row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    flex-wrap: wrap;
  }
`;

const EditSubjectsButton = styled(SmallButton)`
  border-color: #8b5cf6;
  background: #f5f3ff;
  color: #7c3aed;

  &:hover:not(:disabled) {
    background: #ede9fe;
  }
`;

const SubjectEditSection = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: #faf5ff;
  border: 1px solid #e9d5ff;
  border-radius: 6px;
`;

const SubjectEditTitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #7c3aed;
  margin-bottom: 8px;
`;

const SubjectEditActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`;

// Relief card: header row (teacher + schedule, "+ Add Week") above one row per covered
// week, each with its own red Remove button so a single week can be cancelled on its own.
const ReliefCard = styled(TeacherCard)`
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
`;

const ReliefCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ReliefWeekRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-top: 1px solid #dbeafe;
`;

const TeacherAssignmentModal = ({
  show,
  onClose,
  courseGroup,
  onUpdate,
  onReliefChange
}) => {
  const [assignedTeachers, setAssignedTeachers] = useState([]);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  // 'primary' = whole term, lead teacher | 'normal' = whole term, not lead |
  // 'relief' = one week only, opens the relief popup instead of adding directly
  const [teacherMode, setTeacherMode] = useState('normal');
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);

  // Edit subjects state
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [editSubjectIds, setEditSubjectIds] = useState([]);

  // Relief (substitute teacher) state
  const [courseWeeks, setCourseWeeks] = useState([]);
  const [showReliefPopup, setShowReliefPopup] = useState(false);
  const [reliefScheduleIndex, setReliefScheduleIndex] = useState('');
  const [reliefWeek, setReliefWeek] = useState('');
  const [savingRelief, setSavingRelief] = useState(false);

  // Get all subjects from course group (flattened for checkboxes)
  const courseGroupSubjects = React.useMemo(() => {
    const subjects = courseGroup?.subjects || [];
    const result = [];
    subjects.forEach(parent => {
      result.push({ id: parent.subjectId, name: parent.subjectName, isParent: true });
      (parent.children || []).forEach(child => {
        result.push({ id: child.subjectId, name: child.subjectName, isParent: false, parentName: parent.subjectName });
      });
    });
    return result;
  }, [courseGroup?.subjects]);

  // Name lookup for any teacher (assigned or available), used to label relief entries
  const teacherNameById = React.useMemo(() => {
    const map = {};
    assignedTeachers.forEach(t => { map[t.teacherId] = t.teacherName; });
    availableTeachers.forEach(t => { map[t.id] = t.name; });
    return map;
  }, [assignedTeachers, availableTeachers]);

  const schedules = React.useMemo(() => courseGroup?.schedules || [], [courseGroup?.schedules]);

  // Schedule rows with no main teacher (e.g. after that teacher was removed from the
  // group). Materials are only shown to a booking's effective teacher, so until one is
  // set nobody can see them except in that row's relief weeks.
  const unstaffedSchedules = React.useMemo(() => (
    isSpecialTerm(courseGroup?.term)
      ? []
      : schedules.map((schedule, index) => ({ schedule, index })).filter(({ schedule }) => !schedule.teacherId)
  ), [schedules, courseGroup?.term]);
  const [mainTeacherPick, setMainTeacherPick] = useState({});

  /**
   * What taking this class would ask of a teacher: the year, and every booking it runs in.
   *
   * Used to order the picker. A course group that meets twice a week is offered to anyone free
   * for either of them — the schedule a relief teacher actually covers is chosen further down,
   * and the popup re-ranks against just that one.
   */
  const courseGroupNeeds = React.useMemo(() => schedules.map(s => ({
    dayOfWeek: s.dayOfWeek,
    startTime: s.startTime,
    endTime: s.endTime,
    year: courseGroup?.year,
    subjectIds: (s.subjectIds && s.subjectIds.length ? s.subjectIds : [s.subjectId]).filter(Boolean)
  })), [schedules, courseGroup?.year]);

  /** The one booking a relief teacher would be covering, once it has been picked. */
  const reliefNeed = React.useMemo(() => {
    if (reliefScheduleIndex === '') return null;
    const schedule = schedules[Number(reliefScheduleIndex)];
    if (!schedule) return null;
    return {
      dayOfWeek: schedule.dayOfWeek,
      startTime: schedule.startTime,
      endTime: schedule.endTime,
      year: courseGroup?.year,
      subjectIds: (schedule.subjectIds && schedule.subjectIds.length
        ? schedule.subjectIds
        : [schedule.subjectId]).filter(Boolean)
    };
  }, [schedules, reliefScheduleIndex, courseGroup?.year]);

  /**
   * Everyone who could be picked, best fit first.
   *
   * Relief may target a teacher already on this course group (a subject co-teacher covering
   * another schedule for one week); Primary and Normal only offer people not yet assigned.
   * Nobody is filtered out by their availability — it only decides the order and the note.
   */
  const teacherChoices = React.useMemo(() => {
    const pool = teacherMode === 'relief'
      ? [
        ...assignedTeachers.map(t => ({ ...t, id: t.teacherId, name: t.teacherName })),
        ...availableTeachers
      ]
      : availableTeachers;
    const needs = reliefNeed ? [reliefNeed] : courseGroupNeeds;
    return [...pool].sort(byBestMatch(needs, t => t.name || t.email || ''));
  }, [teacherMode, assignedTeachers, availableTeachers, reliefNeed, courseGroupNeeds]);

  /** Stay quiet about availability until somebody has actually filled theirs in. */
  const showAvailabilityHints = React.useMemo(
    () => anyoneHasAvailability(teacherChoices),
    [teacherChoices]
  );

  // Flatten schedules[].reliefAssignments into one list for display
  const reliefEntries = React.useMemo(() => {
    const entries = [];
    schedules.forEach((schedule, scheduleIndex) => {
      (schedule.reliefAssignments || []).forEach(relief => {
        entries.push({
          scheduleIndex,
          week: relief.week,
          teacherId: relief.teacherId,
          scheduleLabel: `${DAY_LABELS[schedule.dayOfWeek] || `Day ${schedule.dayOfWeek}`} ${schedule.startTime}-${schedule.endTime}`
        });
      });
    });
    entries.sort((a, b) => a.week - b.week);
    return entries;
  }, [schedules]);

  // Group reliefEntries by (teacherId, scheduleIndex) so the list shows one card per
  // relief teacher/schedule with all their weeks together, instead of one card per week —
  // this is what lets an existing relief teacher get more weeks added or removed in place.
  const reliefGroups = React.useMemo(() => {
    const map = new Map();
    reliefEntries.forEach(entry => {
      const key = `${entry.teacherId}-${entry.scheduleIndex}`;
      if (!map.has(key)) {
        map.set(key, {
          teacherId: entry.teacherId,
          scheduleIndex: entry.scheduleIndex,
          scheduleLabel: entry.scheduleLabel,
          weeks: []
        });
      }
      map.get(key).weeks.push(entry.week);
    });
    return Array.from(map.values());
  }, [reliefEntries]);

  // Weeks already covered by a relief entry on the currently-selected schedule (by any
  // teacher) — a schedule/week pair can only have one relief teacher, so these are hidden
  // from the picker instead of letting a second pick silently overwrite the first.
  const takenWeeksForSchedule = React.useMemo(() => {
    if (reliefScheduleIndex === '') return new Set();
    const schedule = schedules[Number(reliefScheduleIndex)];
    return new Set((schedule?.reliefAssignments || []).map(r => Number(r.week)));
  }, [schedules, reliefScheduleIndex]);

  // When the relief popup opens (either from "Continue" or from "+ Add Week" further down
  // the modal), scroll it into view — it always renders inside the Add Teacher section.
  const reliefPopupRef = React.useRef(null);
  useEffect(() => {
    if (showReliefPopup && reliefPopupRef.current) {
      reliefPopupRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showReliefPopup]);

  // Load data
  const loadData = useCallback(async () => {
    if (!courseGroup?.id) return;

    setLoading(true);
    setError(null);

    try {
      const [assignedData, allTeachersData, weeksData] = await Promise.all([
        listTeachers(courseGroup.id),
        listAvailableTeachers(courseGroup.campusId),
        calculateWeeks(courseGroup.id)
      ]);

      setAssignedTeachers(assignedData);
      setCourseWeeks(weeksData?.weeks || []);

      // Filter out already assigned teachers
      const assignedIds = assignedData.map(t => t.teacherId);
      const available = allTeachersData.filter(t => !assignedIds.includes(t.id));
      setAvailableTeachers(available);

    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [courseGroup?.id, courseGroup?.campusId]);

  useEffect(() => {
    if (show) {
      loadData();
    }
  }, [show, loadData]);

  // Handle add teacher. Primary/Normal add directly (whole term); Relief opens the
  // schedule+week popup instead — the actual setScheduleRelief call happens on confirm.
  const handleAdd = async () => {
    if (!selectedTeacherId) {
      alert('Please select a teacher to add');
      return;
    }

    if (teacherMode === 'relief') {
      setShowReliefPopup(true);
      return;
    }

    setSaving(true);
    try {
      await addTeacher(courseGroup.id, selectedTeacherId, teacherMode === 'primary', selectedSubjectIds);
      await loadData();
      const addedTeacherId = selectedTeacherId;
      const addedSubjectIds = [...selectedSubjectIds];
      setSelectedTeacherId('');
      setTeacherMode('normal');
      setSelectedSubjectIds([]);
      if (onUpdate) onUpdate({ teacherId: addedTeacherId, subjectIds: addedSubjectIds });
    } catch (err) {
      console.error('Error adding teacher:', err);
      alert(`Failed to add teacher: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Toggle subject selection (for adding)
  const toggleSubject = (subjectId) => {
    if (selectedSubjectIds.includes(subjectId)) {
      setSelectedSubjectIds(selectedSubjectIds.filter(id => id !== subjectId));
    } else {
      setSelectedSubjectIds([...selectedSubjectIds, subjectId]);
    }
  };

  // Start editing subjects for a teacher
  const startEditSubjects = (assignment) => {
    setEditingAssignmentId(assignment.id);
    setEditSubjectIds(assignment.subjectIds || []);
  };

  // Cancel editing subjects
  const cancelEditSubjects = () => {
    setEditingAssignmentId(null);
    setEditSubjectIds([]);
  };

  // Toggle subject selection (for editing)
  const toggleEditSubject = (subjectId) => {
    if (editSubjectIds.includes(subjectId)) {
      setEditSubjectIds(editSubjectIds.filter(id => id !== subjectId));
    } else {
      setEditSubjectIds([...editSubjectIds, subjectId]);
    }
  };

  // Save edited subjects
  const saveEditedSubjects = async () => {
    if (!editingAssignmentId) return;

    setSaving(true);
    try {
      await updateTeacherSubjects(editingAssignmentId, editSubjectIds);
      await loadData();
      setEditingAssignmentId(null);
      setEditSubjectIds([]);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error updating teacher subjects:', err);
      alert(`Failed to update subjects: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle toggle primary
  const handleTogglePrimary = async (assignment) => {
    setSaving(true);
    try {
      await updateAssignment(assignment.id, { isPrimary: !assignment.isPrimary });
      await loadData();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error updating teacher:', err);
      alert(`Failed to update teacher: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Set the main (whole-term) teacher of a schedule row that has none. Re-reads the
  // course group first because schedules[] is saved as a whole array, so a stale copy
  // would overwrite relief weeks set since this modal opened.
  const handleSetMainTeacher = async (schedule, index) => {
    const teacherId = mainTeacherPick[index];
    if (!teacherId) {
      alert('Please select a teacher');
      return;
    }

    setSaving(true);
    try {
      const fresh = await getCourseGroup(courseGroup.id);
      const updatedSchedules = (fresh.schedules || []).map((row, i) => {
        const isTarget = schedule.scheduleId ? row.scheduleId === schedule.scheduleId : i === index;
        return isTarget ? { ...row, teacherId } : row;
      });
      await updateCourseGroup(courseGroup.id, { schedules: updatedSchedules });
      setMainTeacherPick(prev => ({ ...prev, [index]: '' }));
      if (onReliefChange) await onReliefChange();
    } catch (err) {
      console.error('Error setting main teacher:', err);
      alert(`Failed to set main teacher: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle remove teacher
  const handleRemove = async (assignment) => {
    // Removing a teacher also clears them as main teacher of any schedule row they hold,
    // which leaves that row's materials visible to nobody — say so before it happens.
    const heldRows = isSpecialTerm(courseGroup?.term)
      ? []
      : schedules.filter(s => s.teacherId && s.teacherId === assignment.teacherId);
    const heldNote = heldRows.length > 0
      ? `\n\nThey are the main teacher of ${heldRows.map(s => `${DAY_LABELS[s.dayOfWeek] || ''} ${s.startTime}-${s.endTime}`).join(', ')}. `
        + 'That schedule will have no main teacher until you set a new one, and no teacher will see its materials. '
        + 'If they are only away for some weeks, cancel and add a Relief teacher for those weeks instead.'
      : '';
    if (!window.confirm(`Are you sure you want to remove ${assignment.teacherName || 'this teacher'} from this course group?${heldNote}`)) {
      return;
    }

    setSaving(true);
    try {
      await removeTeacher(assignment.id);
      await loadData();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error removing teacher:', err);
      alert(`Failed to remove teacher: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle confirm relief (from the popup opened by handleAdd when mode is 'relief')
  const handleConfirmRelief = async () => {
    if (reliefScheduleIndex === '' || !reliefWeek || !selectedTeacherId) {
      alert('Please select a schedule and week');
      return;
    }

    setSavingRelief(true);
    try {
      await setScheduleRelief(courseGroup.id, Number(reliefScheduleIndex), Number(reliefWeek), selectedTeacherId);
      setShowReliefPopup(false);
      setReliefScheduleIndex('');
      setReliefWeek('');
      setSelectedTeacherId('');
      setTeacherMode('normal');
      await loadData();
      if (onReliefChange) await onReliefChange();
    } catch (err) {
      console.error('Error setting relief:', err);
      alert(`Failed to set relief: ${err.message}`);
    } finally {
      setSavingRelief(false);
    }
  };

  // Handle cancel from the relief popup (does not remove the teacher selection/mode)
  const handleCancelReliefPopup = () => {
    setShowReliefPopup(false);
    setReliefScheduleIndex('');
    setReliefWeek('');
  };

  // Add another week for a relief teacher/schedule pair that already has at least one
  // week — pre-fills the popup so the admin only needs to pick the new week.
  const handleAddWeekForRelief = (teacherId, scheduleIndex) => {
    setSelectedTeacherId(teacherId);
    setTeacherMode('relief');
    setReliefScheduleIndex(String(scheduleIndex));
    setReliefWeek('');
    setShowReliefPopup(true);
  };

  // Handle cancel relief
  const handleClearRelief = async (scheduleIndex, week) => {
    if (!window.confirm(`Cancel the relief assignment for week ${week}?`)) {
      return;
    }

    setSavingRelief(true);
    try {
      await clearScheduleRelief(courseGroup.id, scheduleIndex, week);
      await loadData();
      if (onReliefChange) await onReliefChange();
    } catch (err) {
      console.error('Error clearing relief:', err);
      alert(`Failed to cancel relief: ${err.message}`);
    } finally {
      setSavingRelief(false);
    }
  };

  if (!show) return null;

  const courseGroupName = courseGroup ? `${courseGroup.title} Y${courseGroup.year}T${courseGroup.term}` : '';

  return (
    <ModalOverlay>
      <Modal>
        <ModalHeader>
          <h3>Manage Teachers - {courseGroupName}</h3>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalContent>
          {error && <ErrorState>{error}</ErrorState>}

          {loading ? (
            <LoadingState>Loading...</LoadingState>
          ) : (
            <>
              {unstaffedSchedules.length > 0 && (
                <UnstaffedWarning>
                  <strong>No main teacher set.</strong> No teacher can see the materials of these
                  schedules (except in their relief weeks) until a main teacher is set.
                  {unstaffedSchedules.map(({ schedule, index }) => (
                    <div className="row" key={schedule.scheduleId || index}>
                      <span style={{ minWidth: '120px', fontWeight: 600 }}>
                        {DAY_LABELS[schedule.dayOfWeek] || ''} {schedule.startTime}-{schedule.endTime}
                      </span>
                      <Select
                        style={{ flex: '1 1 160px', maxWidth: '240px' }}
                        value={mainTeacherPick[index] || ''}
                        onChange={(e) => setMainTeacherPick(prev => ({ ...prev, [index]: e.target.value }))}
                        disabled={saving}
                      >
                        <option value="">Select a teacher...</option>
                        {assignedTeachers.map(t => (
                          <option key={t.teacherId} value={t.teacherId}>
                            {t.teacherName || t.teacherEmail || t.teacherId}
                          </option>
                        ))}
                      </Select>
                      <Button
                        onClick={() => handleSetMainTeacher(schedule, index)}
                        disabled={saving || !mainTeacherPick[index]}
                      >
                        Set as Main Teacher
                      </Button>
                    </div>
                  ))}
                  {assignedTeachers.length === 0 && (
                    <div className="row">Add a teacher to this course group first.</div>
                  )}
                </UnstaffedWarning>
              )}

              {/* Add Teacher Section */}
              <Section>
                <SectionTitle>Add Teacher</SectionTitle>
                <FormRow>
                  <FormGroup>
                    <label>Select a teacher</label>
                    <Select
                      value={selectedTeacherId}
                      onChange={(e) => setSelectedTeacherId(e.target.value)}
                    >
                      <option value="">Select a teacher...</option>
                      {/* Ordered by who can actually take it: free at that hour, teaching that
                          year and those subjects. Everyone stays in the list — a teacher who
                          has filled nothing in, or who is busy, can still be picked. */}
                      {teacherChoices.map(teacher => {
                        const name = teacher.name || teacher.email;
                        return (
                          <option key={teacher.id} value={teacher.id}>
                            {showAvailabilityHints
                              ? teacherOptionLabel(teacher, reliefNeed || courseGroupNeeds, name)
                              : name}
                          </option>
                        );
                      })}
                    </Select>
                    {showAvailabilityHints && (
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                        Sorted by who is free for this class. Anyone can still be chosen.
                      </div>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <label>Role</label>
                    <div style={{ display: 'flex', gap: '10px', height: '38px', alignItems: 'center' }}>
                      <CheckboxLabel>
                        <input
                          type="radio"
                          name="teacherMode"
                          checked={teacherMode === 'primary'}
                          onChange={() => setTeacherMode('primary')}
                        />
                        Primary
                      </CheckboxLabel>
                      <CheckboxLabel>
                        <input
                          type="radio"
                          name="teacherMode"
                          checked={teacherMode === 'normal'}
                          onChange={() => setTeacherMode('normal')}
                        />
                        Normal
                      </CheckboxLabel>
                      <CheckboxLabel>
                        <input
                          type="radio"
                          name="teacherMode"
                          checked={teacherMode === 'relief'}
                          onChange={() => setTeacherMode('relief')}
                        />
                        Relief
                      </CheckboxLabel>
                    </div>
                  </FormGroup>
                  <Button
                    onClick={handleAdd}
                    disabled={saving || !selectedTeacherId}
                  >
                    {teacherMode === 'relief' ? 'Continue' : 'Add'}
                  </Button>
                </FormRow>

                {teacherMode === 'relief' && (
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}>
                    Relief covers one week only — pick the schedule and week next. The regular
                    teacher automatically regains it the following week, no manual switch-back.
                  </div>
                )}

                {/* Relief popup: appears after clicking Continue in relief mode, or
                    "+ Add Week" on an existing relief entry further down */}
                {showReliefPopup && (
                  <SubjectEditSection ref={reliefPopupRef}>
                    <SubjectEditTitle>
                      Relief for {teacherNameById[selectedTeacherId] || 'this teacher'} — pick the schedule and week
                    </SubjectEditTitle>
                    <FormRow>
                      <FormGroup>
                        <label>Schedule</label>
                        <Select
                          value={reliefScheduleIndex}
                          onChange={(e) => { setReliefScheduleIndex(e.target.value); setReliefWeek(''); }}
                        >
                          <option value="">Select a schedule...</option>
                          {schedules.map((schedule, idx) => (
                            <option key={idx} value={idx}>
                              {DAY_LABELS[schedule.dayOfWeek] || `Day ${schedule.dayOfWeek}`} {schedule.startTime}-{schedule.endTime}
                              {' '}(currently {teacherNameById[schedule.teacherId] || 'unassigned'})
                            </option>
                          ))}
                        </Select>
                      </FormGroup>
                      <FormGroup>
                        <label>Week</label>
                        <Select
                          value={reliefWeek}
                          onChange={(e) => setReliefWeek(e.target.value)}
                        >
                          <option value="">Select a week...</option>
                          {courseWeeks
                            .filter(w => !w.isHoliday && !takenWeeksForSchedule.has(w.week))
                            .map(week => (
                              <option key={week.week} value={week.week}>
                                Week {week.week} ({week.startDate} - {week.endDate})
                              </option>
                            ))}
                        </Select>
                        {reliefScheduleIndex !== '' && takenWeeksForSchedule.size > 0 && (
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                            Already covered: week {Array.from(takenWeeksForSchedule).sort((a, b) => a - b).join(', ')}
                          </div>
                        )}
                      </FormGroup>
                    </FormRow>

                    {/* The teacher was picked before the schedule was, so this is the first
                        moment anything can be said about whether they are free for it. Said,
                        not enforced — relief exists precisely because somebody has to cover a
                        class at a time that suits nobody. */}
                    {showAvailabilityHints && reliefNeed && selectedTeacherId && (() => {
                      const teacher = teacherChoices.find(c => c.id === selectedTeacherId);
                      if (!teacher) return null;
                      const match = matchTeacher(teacher, reliefNeed);
                      const free = match.time === true;
                      return (
                        <div style={{
                          fontSize: '12px', marginTop: '4px',
                          color: free ? '#047857' : '#b45309'
                        }}>
                          {teacherNameById[selectedTeacherId] || 'This teacher'} is{' '}
                          {free ? 'free for this class' : matchNote(match, reliefNeed)}.
                          {!free && ' You can still assign them.'}
                        </div>
                      );
                    })()}
                    <SubjectEditActions>
                      <SmallButton onClick={handleCancelReliefPopup} disabled={savingRelief}>
                        Cancel
                      </SmallButton>
                      <Button
                        onClick={handleConfirmRelief}
                        disabled={savingRelief || reliefScheduleIndex === '' || !reliefWeek}
                      >
                        Confirm Relief
                      </Button>
                    </SubjectEditActions>
                  </SubjectEditSection>
                )}

                {/* Subject Selection for New Teacher (not relevant for relief — the chosen
                    schedule above already scopes it) */}
                {teacherMode !== 'relief' && courseGroupSubjects.length > 0 && (
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 500, color: '#6b7280' }}>
                      Assign Subjects (optional)
                    </label>
                    <SubjectCheckboxContainer>
                      {courseGroupSubjects.map(subject => (
                        <SubjectCheckboxLabel
                          key={subject.id}
                          $checked={selectedSubjectIds.includes(subject.id)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSubjectIds.includes(subject.id)}
                            onChange={() => toggleSubject(subject.id)}
                          />
                          {subject.isParent ? subject.name : `${subject.parentName} - ${subject.name}`}
                        </SubjectCheckboxLabel>
                      ))}
                    </SubjectCheckboxContainer>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                      Leave empty to allow admin to assign subjects later.
                    </div>
                  </div>
                )}

                {teacherMode !== 'relief' && availableTeachers.length === 0 && (
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    All available teachers have been assigned to this course group.
                  </div>
                )}
              </Section>

              {/* Assigned Teachers Section */}
              <Section>
                <SectionTitle>Assigned Teachers ({assignedTeachers.length})</SectionTitle>
                {assignedTeachers.length === 0 ? (
                  <EmptyState>
                    No teachers assigned to this course group yet.
                  </EmptyState>
                ) : (
                  assignedTeachers.map(assignment => (
                    <TeacherCard key={assignment.id}>
                      <TeacherInfo>
                        <h5>
                          {assignment.teacherName || 'Unknown Teacher'}
                          {assignment.isPrimary && (
                            <Badge $bg="#dcfce7" $color="#166534">Primary</Badge>
                          )}
                        </h5>
                        <div className="meta">
                          {assignment.teacherEmail || ''}
                        </div>

                        {/* Subject Tags */}
                        {assignment.subjectNames && assignment.subjectNames.length > 0 ? (
                          <SubjectTags>
                            {assignment.subjectNames.map((name, idx) => (
                              <SubjectTag key={idx}>{name}</SubjectTag>
                            ))}
                          </SubjectTags>
                        ) : courseGroupSubjects.length > 0 ? (
                          <NoSubjectsWarning>No subjects assigned</NoSubjectsWarning>
                        ) : null}

                        {/* Edit Subject Section */}
                        {editingAssignmentId === assignment.id && (
                          <SubjectEditSection>
                            <SubjectEditTitle>Edit Subjects</SubjectEditTitle>
                            <SubjectCheckboxContainer>
                              {courseGroupSubjects.map(subject => (
                                <SubjectCheckboxLabel
                                  key={subject.id}
                                  $checked={editSubjectIds.includes(subject.id)}
                                >
                                  <input
                                    type="checkbox"
                                    checked={editSubjectIds.includes(subject.id)}
                                    onChange={() => toggleEditSubject(subject.id)}
                                  />
                                  {subject.isParent ? subject.name : `${subject.parentName} - ${subject.name}`}
                                </SubjectCheckboxLabel>
                              ))}
                            </SubjectCheckboxContainer>
                            <SubjectEditActions>
                              <SmallButton onClick={cancelEditSubjects} disabled={saving}>
                                Cancel
                              </SmallButton>
                              <Button onClick={saveEditedSubjects} disabled={saving}>
                                Save
                              </Button>
                            </SubjectEditActions>
                          </SubjectEditSection>
                        )}

                        <AssignedByInfo>
                          Assigned by {assignment.assignedByName || 'Unknown'} on {new Date(assignment.createdAt).toLocaleDateString()}
                        </AssignedByInfo>
                      </TeacherInfo>
                      <ActionButtons>
                        {courseGroupSubjects.length > 0 && editingAssignmentId !== assignment.id && (
                          <EditSubjectsButton
                            onClick={() => startEditSubjects(assignment)}
                            disabled={saving}
                          >
                            Edit Subjects
                          </EditSubjectsButton>
                        )}
                        <SmallButton
                          onClick={() => handleTogglePrimary(assignment)}
                          disabled={saving}
                        >
                          {assignment.isPrimary ? 'Remove Primary' : 'Make Primary'}
                        </SmallButton>
                        <RemoveButton
                          onClick={() => handleRemove(assignment)}
                          disabled={saving}
                        >
                          Remove
                        </RemoveButton>
                      </ActionButtons>
                    </TeacherCard>
                  ))
                )}
              </Section>

              {/* Relief Assignments Section — one card per relief teacher/schedule, with
                  every covered week on its own row with a red Remove button, and a button
                  to add more weeks for the same teacher without re-picking them from the
                  top form */}
              {!isSpecialTerm(courseGroup?.term) && schedules.length > 0 && (
                <Section>
                  <SectionTitle>Relief Assignments</SectionTitle>

                  {reliefGroups.length === 0 ? (
                    <EmptyState>No relief assignments scheduled.</EmptyState>
                  ) : (
                    reliefGroups.map(group => (
                      <ReliefCard key={`${group.teacherId}-${group.scheduleIndex}`}>
                        <ReliefCardHeader>
                          <TeacherInfo>
                            <h5>{teacherNameById[group.teacherId] || 'Unknown Teacher'}</h5>
                            <div className="meta">{group.scheduleLabel}</div>
                          </TeacherInfo>
                          <ActionButtons>
                            <SmallButton
                              onClick={() => handleAddWeekForRelief(group.teacherId, group.scheduleIndex)}
                              disabled={savingRelief}
                            >
                              + Add Week
                            </SmallButton>
                          </ActionButtons>
                        </ReliefCardHeader>
                        {group.weeks.sort((a, b) => a - b).map(week => (
                          <ReliefWeekRow key={week}>
                            <Badge $bg="#fef3c7" $color="#92400e">Week {week}</Badge>
                            <RemoveButton
                              onClick={() => handleClearRelief(group.scheduleIndex, week)}
                              disabled={savingRelief}
                            >
                              Remove
                            </RemoveButton>
                          </ReliefWeekRow>
                        ))}
                      </ReliefCard>
                    ))
                  )}
                </Section>
              )}
            </>
          )}
        </ModalContent>

        <ModalFooter>
          <Button $color="#6b7280" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </ModalOverlay>
  );
};

export default TeacherAssignmentModal;
